import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { UnProtectedRoute } from "./UnProtectedRoute";
import Login from "views/Authentication/Login";
import OverviewSummary from "views/Overview/Summary";
import OverviewCharts from "views/Overview/Charts";
import Register from "views/Authentication/Register";
import Readings from "views/Dashboard/Reports/Readings";
import SavedQueries from "views/Dashboard/Reports/SavedQueries";
import EditSavedQuery from "views/Dashboard/Reports/SavedQueries/EditSavedQuery";
import ManageSamples from "views/Dashboard/Forecasts/ConsumptionForecasts";
import CreateConsumptionForecast from "views/Dashboard/Forecasts/ConsumptionForecasts/ForecastsTable/CreateConsumptionForecast";
import EditSample from "views/Dashboard/Forecasts/ConsumptionForecasts/EditSample";
import SavedSamples from "views/Dashboard/Forecasts/SavedSamples";
import EditSavedSample from "views/Dashboard/Forecasts/SavedSamples/EditSavedSample";
import Clients from "views/Dashboard/Registry/Clients";
import EditClient from "views/Dashboard/Registry/EditClient";
import Contracts from "views/Dashboard/Registry/Contracts";
import MeteringPoints from "views/Dashboard/Registry/MeteringPoints";
import GuaranteesOfOriginCertificates from "views/GuaranteesOfOrigin/Certificates";
import GuaranteesOfOriginSummary from "views/GuaranteesOfOrigin/Summary";
import Users from "views/Dashboard/Settings/Users";
import Operators from "views/Dashboard/Settings/Operators";
import ForecaSettings from "views/Dashboard/Settings/ForecaSettings";
import ElevationCoefficents from "views/Dashboard/Settings/ElevationCoefficents";
import ProductionParks from "views/Dashboard/Registry/ProductionParks";
import AddProductionPark from "views/Dashboard/Registry/ProductionParks/AddProductionPark";
import EditProductionPark from "views/Dashboard/Registry/ProductionParks/EditProductionPark/EditProductionPark";
import Litgrid from "views/Dashboard/Settings/Litgrid";
import UserProfile from "views/Dashboard/Settings/UserProfile";
import Unauthorized from "views/Authentication/Unauthorized/Unauthorized";
import SupplyPlanOrders from "views/Dashboard/SupplyPlans/Orders";
import SupplyPlanResults from "views/Dashboard/SupplyPlans/Results";
import ClientReports from "views/Dashboard/ClientReports";
import ClientReportsSummary from "views/Dashboard/ClientReports/Summary";
import ClientReportsForecast from "views/Dashboard/ClientReports/Forecast";
import ProductionForecast from "views/Dashboard/Forecasts/Production";
import ProductionForecastGroup from "views/Dashboard/Forecasts/Production/ProductionForecastGroup";
import Batteries from "views/Dashboard/Forecasts/Batteries";
import Portfolios from "views/NordPool/Portfolios/PortfoliosTable";
import EditPortfolio from "views/NordPool/Portfolios/EditPortfolio";
import NordPoolSupplyPlans from "views/NordPool/SupplyPlans";
import ViewNordPoolSupplyPlan from "views/NordPool/SupplyPlans/ViewSupplyPlan";
import NordPoolResults from "views/NordPool/Results";
import NordPoolSettings from "views/NordPool/Settings";
import GOGroups from "views/Dashboard/Settings/GOGroups";
import BatteryGroups from "views/Dashboard/Settings/BatteryGroups";
import EditBatteryGroup from "views/Dashboard/Settings/BatteryGroups/EditBatteryGroup";
import ClientGroups from "views/Dashboard/Settings/ClientGroups";
import Fees from "views/Dashboard/Settings/Fees";
import BalancePlans from "views/NordPool/BalancePlans";
import NordPoolIdcTransactions from "views/NordPool/IdcTransactions";
import NotFound from "views/NotFound";
import RouteError from "views/RouteError";

export enum UnprotectedPaths {
  "Login" = "login",
  "Register" = "register",
  "Unauthorized" = "unauthorized",
}

export enum ProtectedPaths {
  "OverviewSummary" = "overview/summary",
  "OverviewCharts" = "overview/charts",
  "Readings" = "reports/readings",
  "SavedQueries" = "reports/saved-queries",
  "EditSavedQuery" = "reports/edit-query",
  "CreateConsumptionForecast" = "forecasts/consumption/create",
  "EditConsumptionForecast" = "forecasts/consumption/edit",
  "SavedSamples" = "forecasts/saved-samples",
  "EditSavedSample" = "forecasts/saved-samples/edit",
  "Consumption" = "forecasts/consumption",
  "Production" = "forecasts/production",
  "Batteries" = "forecasts/batteries",
  "SupplyPlans" = "supply-plans",
  "SupplyPlanOrders" = "supply-plans/orders",
  "SupplyPlanResults" = "supply-plans/results",
  "Clients" = "registry/clients",
  "MeteringPoints" = "registry/metering-points",
  "ClientContracts" = "registry/contracts",
  "ProductionParks" = "registry/production-parks",
  "AddProductionParks" = "registry/add/production-parks",
  "EditProductionParks" = "registry/edit/production-parks",
  "GuaranteesOfOriginCertificates" = "registry/guarantees-of-origin/certificates",
  "GuaranteesOfOriginSummary" = "registry/guarantees-of-origin/summary",
  "Users" = "settings/users",
  "Operators" = "settings/operators",
  "ForecaSettings" = "settings/foreca-coefficents",
  "ElevationCoefficents" = "settings/elevation-coefficents",
  "AccountSettings" = "account-settings",
  "Litgrid" = "settings/litgrid",
  "UserProfile" = "settings/user-profile",
  "ClientReports" = "dashboard/reports",
  "ClientReportsSummary" = "dashboard/reports/summary",
  "ClientReportsForecast" = "dashboard/reports/forecast",
  "Portfolios" = "nordpool/portfolios",
  "NordPoolSupplyPlans" = "nordpool/supply-plans",
  "NordPoolResults" = "nordpool/results",
  "NordPoolSettings" = "nordpool/settings",
  "BalancePlans" = "nordpool/balance-plans",
  "NordPoolIdc" = "nordpool/idc",
  "GOGroups" = "settings/go-groups",
  "BatteryGroups" = "settings/battery-groups",
  "EditBatteryGroup" = "settings/battery-groups/edit",
  "ClientGroups" = "settings/client-groups",
  "Fees" = "settings/fees",
}

export default () => {
  const unauthenticatedRoutes = [
    {
      path: "/login",
      element: (
        <UnProtectedRoute>
          <Login />
        </UnProtectedRoute>
      ),
    },
    {
      path: "/register",
      element: <Register />,
    },
    {
      path: "/unauthorized",
      element: <Unauthorized />,
    },
  ];

  const authenticatedRoutes = [
    {
      path: "/",
      element: <ProtectedRoute />,
      children: [
        {
          path: `/${ProtectedPaths.OverviewSummary}`,
          element: <OverviewSummary />,
        },
        {
          path: `/${ProtectedPaths.OverviewCharts}`,
          element: <OverviewCharts />,
        },
        {
          path: ProtectedPaths.Readings,
          element: <Readings />,
        },
        {
          path: ProtectedPaths.SavedQueries,
          element: <SavedQueries />,
        },
        {
          path: `/${ProtectedPaths.EditSavedQuery}/:queryId`,
          element: <EditSavedQuery />,
        },
        {
          path: `/${ProtectedPaths.Consumption}`,
          element: <ManageSamples />,
        },
        {
          path: `/${ProtectedPaths.CreateConsumptionForecast}/:forecastId/:forecastDate`,
          element: <CreateConsumptionForecast />,
        },
        {
          path: `/${ProtectedPaths.EditConsumptionForecast}/:sampleId`,
          element: <EditSample />,
        },
        {
          path: ProtectedPaths.SavedSamples,
          element: <SavedSamples />,
        },
        {
          path: `/${ProtectedPaths.EditSavedSample}/:sampleId?`,
          element: <EditSavedSample />,
        },
        {
          path: ProtectedPaths.Clients,
          element: <Clients />,
        },
        {
          path: `/${ProtectedPaths.Clients}/:clientId`,
          element: <EditClient />,
        },
        {
          path: ProtectedPaths.MeteringPoints,
          element: <MeteringPoints />,
        },
        {
          path: ProtectedPaths.ClientContracts,
          element: <Contracts />,
        },
        {
          path: ProtectedPaths.Users,
          element: <Users />,
        },
        {
          path: `${ProtectedPaths.Users}/:userId`,
          element: <UserProfile />,
        },
        {
          path: ProtectedPaths.Operators,
          element: <Operators />,
        },
        {
          path: ProtectedPaths.ProductionParks,
          element: <ProductionParks />,
        },
        {
          path: ProtectedPaths.AddProductionParks,
          element: <AddProductionPark />,
        },
        {
          path: ProtectedPaths.ForecaSettings,
          element: <ForecaSettings />,
        },
        {
          path: ProtectedPaths.ElevationCoefficents,
          element: <ElevationCoefficents />,
        },
        {
          path: `${ProtectedPaths.EditProductionParks}/:productionParkId`,
          element: <EditProductionPark />,
        },
        {
          path: ProtectedPaths.Litgrid,
          element: <Litgrid />,
        },
        {
          path: ProtectedPaths.ClientReports,
          element: <ClientReports />,
        },
        {
          path: ProtectedPaths.ClientReportsSummary,
          element: <ClientReportsSummary />,
        },
        {
          path: ProtectedPaths.ClientReportsForecast,
          element: <ClientReportsForecast />,
        },
        {
          path: `${ProtectedPaths.SupplyPlans}/:date?/orders`,
          element: <SupplyPlanOrders />,
        },
        {
          path: `${ProtectedPaths.SupplyPlans}/:date?/results`,
          element: <SupplyPlanResults />,
        },
        {
          path: `/${ProtectedPaths.Production}`,
          element: <ProductionForecast type="production" />,
        },
        {
          path: `/${ProtectedPaths.Production}/:id`,
          element: <ProductionForecastGroup type="production" />,
        },
        {
          path: `/${ProtectedPaths.Batteries}`,
          element: <Batteries />,
        },
        {
          path: `/${ProtectedPaths.BalancePlans}`,
          element: <BalancePlans />,
        },
        {
          path: `/${ProtectedPaths.Portfolios}/`,
          element: <Portfolios />,
        },
        {
          path: `/${ProtectedPaths.Portfolios}/:id`,
          element: <EditPortfolio />,
        },
        {
          path: `/${ProtectedPaths.NordPoolSupplyPlans}`,
          element: <NordPoolSupplyPlans />,
        },
        {
          path: `/${ProtectedPaths.NordPoolSupplyPlans}/:id`,
          element: <ViewNordPoolSupplyPlan />,
        },
        {
          path: `/${ProtectedPaths.NordPoolResults}`,
          element: <NordPoolResults />,
        },
        {
          path: `/${ProtectedPaths.NordPoolSettings}`,
          element: <NordPoolSettings />,
        },
        {
          path: `/${ProtectedPaths.NordPoolIdc}`,
          element: <NordPoolIdcTransactions />,
        },
        {
          path: `/${ProtectedPaths.GuaranteesOfOriginCertificates}`,
          element: <GuaranteesOfOriginCertificates />,
        },
        {
          path: `/${ProtectedPaths.GuaranteesOfOriginSummary}`,
          element: <GuaranteesOfOriginSummary />,
        },
        {
          path: `/${ProtectedPaths.GOGroups}`,
          element: <GOGroups />,
        },
        {
          path: `/${ProtectedPaths.BatteryGroups}`,
          element: <BatteryGroups />,
        },
        {
          path: `/${ProtectedPaths.EditBatteryGroup}/:id`,
          element: <EditBatteryGroup />,
        },
        {
          path: `/${ProtectedPaths.ClientGroups}`,
          element: <ClientGroups />,
        },
        {
          path: `/${ProtectedPaths.Fees}`,
          element: <Fees />,
        },
        {
          path: "*",
          element: <NotFound />,
        },
      ],
    },
  ];

  const router = createBrowserRouter([
    {
      errorElement: <RouteError />,
      children: [...unauthenticatedRoutes, ...authenticatedRoutes],
    },
  ]);

  return <RouterProvider router={router} />;
};
